import React, { useEffect, useState } from "react";
import { message, Spin } from "antd";
import {
  UserGet,
  UserDataGet,
  UserTransactionsGet,
  StaffTransactionsGet,
  AdminTotalRevenueGet,
  DevTransactionsGet,
  getPremiumMembers,
  AdminGetWithdrawals,
} from "../../../../Api/CoreApi";

import StatsCards from "../../DashboardSections/StatsCards/StatsCards";
import MemberChart from "../../DashboardSections/MemberChart/MemberChart";
import ProfileStatus from "../../DashboardSections/ProfileStatus/ProfileStatus";
import GenderChart from "../../DashboardSections/GenderChart/GenderChart";
import RecentMembers from "../../DashboardSections/RecentMembers/RecentMembers";
import RecentMatches from "../../DashboardSections/RecentMatches/RecentMatches";
import RecentPayments from "../../DashboardSections/RecentPayments/RecentPayments";
import QuickActions from "../../DashboardSections/QuickActions/QuickActions";

import "./AdminPanel.css";

const MONTH_ORDER = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

function Admin_Panel() {
  // ⭐ FIX 1: Don't read from localStorage in initial state
  const [loading, setLoading] = useState(true);
  const [isAuthReady, setIsAuthReady] = useState(false);  // ⭐ NEW
  
  const [dashboardData, setDashboardData] = useState({
    totalUsers: 0,
    totalProfiles: 0,
    totalMatches: 0,
    premiumMembers: 0,
    totalRevenue: 0,
    pendingWithdrawals: 0,
    pendingWithdrawalsCount: 0,
  });

  const [chartData, setChartData] = useState([]);
  const [genderData, setGenderData] = useState([]);
  const [profileStatusData, setProfileStatusData] = useState([]);
  const [members, setMembers] = useState([]);
  const [payments, setPayments] = useState([]);

  // ============================================
  // HELPER FUNCTIONS (Same as before)
  // ============================================
  const calculateAgeFromDOB = (dob) => {
    if (!dob) return null;
    try {
      const birthDate = new Date(dob);
      if (isNaN(birthDate.getTime())) return null;
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const m = today.getMonth() - birthDate.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) age--;
      return age > 0 ? age : null;
    } catch {
      return null;
    }
  };

  const getGenderCounts = (users) => {
    const male = users.filter(u => u.gender?.toLowerCase() === "male").length;
    const female = users.filter(u => u.gender?.toLowerCase() === "female").length;
    const other = users.length - male - female;
    return { male, female, other };
  };

  const getStatusCounts = (users) => {
    const active = users.filter(u => u.status === "active" || u.is_active === true).length;
    const pending = users.filter(u => u.status === "pending").length;
    const rejected = users.filter(u => u.status === "rejected").length;
    const inactive = users.length - active - pending - rejected;
    return { active, pending, rejected, inactive: Math.max(inactive, 0) };
  };

  const calcPercent = (value, total) => {
    if (total === 0) return 0;
    return parseFloat(((value / total) * 100).toFixed(1));
  };

  const getMonthlyData = (users) => {
    const monthlyCount = MONTH_ORDER.reduce((acc, month) => {
      acc[month] = 0;
      return acc;
    }, {});

    users.reduce((acc, user) => {
      const dateStr = user.created_date || user.date_joined || user.create_date;
      if (dateStr) {
        const date = new Date(dateStr);
        if (!isNaN(date)) {
          const monthName = date.toLocaleString("en-US", { month: "short" });
          if (acc[monthName] !== undefined) {
            acc[monthName]++;
          }
        }
      }
      return acc;
    }, monthlyCount);

    return MONTH_ORDER.map(month => ({
      month,
      members: monthlyCount[month],
    }));
  };

  const isAdminGrant = (tx) => {
    return tx.ref_id && String(tx.ref_id).startsWith('ADMIN-GRANT-');
  };

  // ============================================
  // ⭐ FIX 2: AUTH CHECK - Wait for token to be ready
  // ============================================
  useEffect(() => {
    let mounted = true;
    let attempts = 0;
    const maxAttempts = 20; // 20 x 100ms = 2 seconds max wait

    const checkAuthReady = () => {
      const token = localStorage.getItem('access_token');
      const userId = localStorage.getItem('user_id');
      const role = localStorage.getItem('role');
      let mounted = true;
      
      
      console.log(`🔐 Auth check (attempt ${attempts + 1}):`, {
        hasToken: !!token,
        hasUserId: !!userId,
        role: role
      });

      if (token && userId && mounted) {
        console.log('✅ Auth ready, starting fetch...');
        setIsAuthReady(true);
      } 
      else if (attempts < maxAttempts && mounted) {
        attempts++;
        setTimeout(checkAuthReady, 100);
      }
       else if (mounted) {
        console.error('❌ Auth check failed after max attempts');
        message.error('Authentication failed. Please login again.');
        setLoading(false);
        // Optional: redirect to login
        // window.location.href = '/Admin_Login';
      }
    };

    checkAuthReady();

    return () => {
      mounted = false;
    };
  }, []);

  // ============================================
  // ⭐ FIX 3: FETCH DATA - Only after auth is ready
  // ============================================
  useEffect(() => {
    if (isAuthReady) {
      fetchDashboardData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthReady]);

  // ============================================
  // MAIN FETCH FUNCTION (WITH RETRY LOGIC)
  // ============================================
  const fetchDashboardData = async (retryCount = 0) => {
    const admin_id = localStorage.getItem("user_id");
    const token = localStorage.getItem("access_token");

    console.log('📡 Fetching dashboard data...', { admin_id, hasToken: !!token });

    if (!admin_id || !token) {
      console.warn('⚠️ Missing auth data');
      if (retryCount < 3) {
        console.log(`🔄 Retrying... (${retryCount + 1}/3)`);
        setTimeout(() => fetchDashboardData(retryCount + 1), 500);
        return;
      }
      message.error("Admin ID not found. Please login again.");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      // ⭐ FIX 4: Use Promise.allSettled instead of Promise.all
      // This won't fail if one API fails
      const results = await Promise.allSettled([
        UserGet(),
        UserDataGet(),
        StaffTransactionsGet(),
        UserTransactionsGet(),
        DevTransactionsGet(),
        AdminTotalRevenueGet(),
        getPremiumMembers(),
        AdminGetWithdrawals({}),
      ]);

      // Extract data from results
      const [
        allUsersResult,
        allProfilesResult,
        staffTransactionResult,
        userTransactionResult,
        devTransactionResult,
        adminRevenueResult,
        premiumMembersResult,
        withdrawalsResult,
      ] = results;

      // Log failed requests
      results.forEach((result, index) => {
        const apiNames = [
          'UserGet', 'UserDataGet', 'StaffTransactionsGet',
          'UserTransactionsGet', 'DevTransactionsGet', 'AdminTotalRevenueGet',
          'getPremiumMembers', 'AdminGetWithdrawals'
        ];
        if (result.status === 'rejected') {
          console.error(`❌ ${apiNames[index]} failed:`, result.reason);
        } else {
          console.log(`✅ ${apiNames[index]} success`);
        }
      });

      // ⭐ FIX 5: Check if all critical APIs failed (auth issue)
      const allFailed = results.every(r => r.status === 'rejected');
      if (allFailed) {
        console.error('❌ All APIs failed - likely auth issue');
        if (retryCount < 2) {
          console.log('🔄 Retrying fetch...');
          setTimeout(() => fetchDashboardData(retryCount + 1), 1000);
          return;
        }
        message.error('Failed to load dashboard. Please refresh and login again.');
        setLoading(false);
        return;
      }

      // Get data with fallbacks
      const allUsersResponse = allUsersResult.status === 'fulfilled' ? allUsersResult.value : [];
      const allProfilesResponse = allProfilesResult.status === 'fulfilled' ? allProfilesResult.value : [];
      const staffTransactionResponse = staffTransactionResult.status === 'fulfilled' ? staffTransactionResult.value : [];
      const userTransactionResponse = userTransactionResult.status === 'fulfilled' ? userTransactionResult.value : [];
      const devTransactionResponse = devTransactionResult.status === 'fulfilled' ? devTransactionResult.value : [];
      const withdrawalsResponse = withdrawalsResult.status === 'fulfilled' ? withdrawalsResult.value : [];

      // ============================================
      // USERS PROCESSING
      // ============================================
      const allUsers = Array.isArray(allUsersResponse) ? allUsersResponse : [];
      const allProfiles = Array.isArray(allProfilesResponse) ? allProfilesResponse : [];

      const usersOnly = allUsers
        .filter(u => u.role === "user")
        .map(u => {
          const profile = allProfiles.find(p =>
            String(p.user_id) === String(u.id) ||
            String(p.User_id) === String(u.id) ||
            String(p.user?.id) === String(u.id) ||
            String(p.username) === String(u.username)
          );

          return {
            ...u,
            ...(profile || {}),
            id: u.id,
            username: u.username,
            first_name: u.first_name,
            role: u.role,
            is_active: u.is_active,
          };
        });

      const totalUsersCount = usersOnly.length;

      // GENDER DATA
      const { male, female, other } = getGenderCounts(usersOnly);
      const totalGender = male + female + other || 1;

      const genderArray = [
        { name: "Male", value: male, percentage: calcPercent(male, totalGender), color: "#1890ff" },
        { name: "Female", value: female, percentage: calcPercent(female, totalGender), color: "#eb2f96" },
      ];

      if (other > 0) {
        genderArray.push({
          name: "Others", value: other, percentage: calcPercent(other, totalGender), color: "#faad14",
        });
      }

      setGenderData(genderArray);

      // PROFILE STATUS
      const { active, pending, rejected, inactive } = getStatusCounts(usersOnly);
      const totalStatus = totalUsersCount || 1;

      setProfileStatusData([
        { name: "Active", value: active, percentage: calcPercent(active, totalStatus), color: "#52c41a" },
        { name: "Inactive", value: inactive, percentage: calcPercent(inactive, totalStatus), color: "#faad14" },
        { name: "Under Verification", value: pending, percentage: calcPercent(pending, totalStatus), color: "#722ed1" },
        { name: "Rejected", value: rejected, percentage: calcPercent(rejected, totalStatus), color: "#f5222d" },
      ]);

      // TRANSACTIONS PROCESSING
      const staffArr = Array.isArray(staffTransactionResponse) ? staffTransactionResponse : [];
      const userArr = Array.isArray(userTransactionResponse) ? userTransactionResponse : [];
      const devArr = Array.isArray(devTransactionResponse) ? devTransactionResponse : [];

      const userData = userArr
        .map(tx => ({ ...tx, _source: "user" }))
        .filter(tx => {
          if (!tx.amount && tx.amount !== 0) return false;
          if (parseFloat(tx.amount) < 0) return false;
          const hasUser = tx.user_name || tx.username || tx.user;
          if (!hasUser) return false;
          return true;
        });

      const staffData = staffArr.map(tx => ({ ...tx, _source: "staff" }));
      const devData = devArr.map(tx => ({ ...tx, _source: "dev" }));

      const allTransactions = [...userData, ...staffData, ...devData];

      // CALCULATE TOTAL REVENUE
      const totalRevenueAmount = allTransactions
        .filter(tx => {
          if (tx._source !== 'user') return false;
          if (tx.type !== 'credit') return false;
          if (tx.plan_name === 'User Referral Bonus') return false;
          if (tx.ref_id && String(tx.ref_id).startsWith('USER-REFER-')) return false;
          if (tx.status !== 'success' && tx.status !== 'approved') return false;
          return true;
        })
        .reduce((sum, tx) => sum + (parseFloat(tx.amount) || 0), 0);

      console.log("💰 Total Revenue:", totalRevenueAmount);

      // PENDING WITHDRAWALS
      const allWithdrawals = Array.isArray(withdrawalsResponse) ? withdrawalsResponse : [];
      const pendingWithdrawals = allWithdrawals.filter(w => w.status === "pending");
      const totalPendingCount = pendingWithdrawals.length;
      const pendingAmount = pendingWithdrawals.reduce(
        (sum, w) => sum + (parseFloat(w.amount) || 0),
        0
      );

      // MONTHLY CHART
      const chartDataFormatted = getMonthlyData(usersOnly);
      setChartData(chartDataFormatted);

      // PREMIUM COUNT
      const premiumCount = usersOnly.filter(u => {
        const isPremium =
          u.is_premium === true ||
          u.premium === true ||
          u.membership_status === 'active';

        if (isPremium && u.plan_expiry) {
          const notExpired = new Date(u.plan_expiry) >= new Date();
          return notExpired;
        }

        return isPremium;
      }).length;

      // DASHBOARD STATS
      setDashboardData({
        totalUsers: totalUsersCount,
        totalProfiles: totalUsersCount,
        totalMatches: Math.floor(totalUsersCount * 0.35),
        premiumMembers: premiumCount,
        totalRevenue: totalRevenueAmount,
        pendingWithdrawals: pendingAmount,
        pendingWithdrawalsCount: totalPendingCount,
      });

      // RECENT MEMBERS
      const sortedUsers = [...usersOnly].sort((a, b) => {
        const dateA = new Date(a.created_date || a.date_joined || 0);
        const dateB = new Date(b.created_date || b.date_joined || 0);
        return dateB - dateA;
      });

      const recentMembers = sortedUsers.slice(0, 5).map(user => {
        let finalAge = user.age;
        if (!finalAge || finalAge === 0 || finalAge === "0") {
          finalAge = calculateAgeFromDOB(user.dob);
        }

        return {
          id: user.id,
          profile_id: user.profile_id,
          staff_id: user.staff_id,
          role: user.role || 'user',
          name: user.first_name || user.username || user.firstname || "N/A",
          firstname: user.firstname || user.first_name,
          username: user.username,
          gender: user.gender || "N/A",
          dob: user.dob || null,
          age: finalAge || "N/A",
          city: user.city || user.district || "N/A",
          district: user.district,
          state: user.state,
          joinDate: user.created_date || user.date_joined || new Date(),
          status: user.status || (user.is_active ? "active" : "pending"),
          is_active: user.is_active,
        };
      });

      setMembers(recentMembers);

      // RECENT PAYMENTS
      const sortedPayments = [...userArr].sort((a, b) => {
        const dateA = new Date(a.created_date || a.date || 0);
        const dateB = new Date(b.created_date || b.date || 0);
        return dateB - dateA;
      });

      const recentPayments = sortedPayments.slice(0, 5).map(tx => ({
        id: tx.id,
        name: tx.user_name || tx.username || "N/A",
        amount: tx.amount || 0,
        date: tx.created_date || tx.date || new Date(),
        status: tx.status === "pending" ? "pending" : "success",
      }));

      setPayments(recentPayments);

      console.log('✅ Dashboard loaded successfully');

    } catch (error) {
      console.error("❌ Dashboard fetch error:", error);
      
      // ⭐ FIX 6: Retry on error
      if (retryCount < 2) {
        console.log(`🔄 Retrying fetch (${retryCount + 1}/2)...`);
        setTimeout(() => fetchDashboardData(retryCount + 1), 1000);
        return;
      }
      
      message.error("Failed to fetch dashboard data");
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // LOADING STATE
  // ============================================
  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "60vh",
          flexDirection: "column",
          gap: "16px",
        }}
      >
        <Spin size="large" />
        <p style={{ color: "#888", margin: 0 }}>
          {isAuthReady ? 'Loading dashboard...' : 'Initializing...'}
        </p>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <StatsCards data={dashboardData} />

      <div className="dashboard-charts-row">
        <div className="chart-col-1">
          <MemberChart data={chartData} />
        </div>
        <div className="chart-col-2">
          <ProfileStatus data={profileStatusData} />
        </div>
        <div className="chart-col-3">
          <GenderChart data={genderData} />
        </div>
      </div>

      <div className="dashboard-tables-row row-1">
        <div className="table-col-1">
          <RecentMembers data={members} />
        </div>
        <div className="table-col-2">
          <RecentMatches />
        </div>
      </div>

      <div className="table-col-payment">
        <RecentPayments data={payments} />
      </div>
    </div>
  );
}

export default Admin_Panel;